/**
 * Hook pour gérer l'export CSV des clients
 * Récupère tous les clients puis leurs détails complets avant l'export
 */

import { useState, useCallback } from "react";
import { clientService } from "../../../../services/clientService";
import clientExportService from "../../../../services/clientExportService";
import { toast } from "react-toastify";

export const useClientExport = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({
    current: 0,
    total: 0,
  });

  /**
   * Récupère les détails complets de tous les clients
   */
  const fetchAllClientDetails = async (clientsList) => {
    const allDetails = [];
    const total = clientsList.length;

    for (let i = 0; i < total; i++) {
      const client = clientsList[i];
      setExportProgress({ current: i + 1, total });

      try {
        const response = await clientService.getClient(client.uuid);
        allDetails.push(response.data);

        // Pause de 250ms entre les requêtes pour respecter la limite de 250 req/min
        // (60000ms / 250 req = 240ms minimum par requête)
        await new Promise((resolve) => setTimeout(resolve, 250));
      } catch (error) {
        console.error(
          `Erreur lors de la récupération du client ${client.uuid}:`,
          error
        );
        toast.error(`Erreur pour le client ${client.name || client.uuid}`);
        // On continue malgré l'erreur
      }
    }

    return allDetails;
  };

  /**
   * Lance l'export complet des clients
   */
  const exportAllClients = useCallback(async () => {
    if (isExporting) {
      return;
    }

    setIsExporting(true);
    setExportProgress({ current: 0, total: 0 });

    try {
      toast.info("Récupération de la liste des clients...");

      // 1. Récupérer la liste de tous les clients
      const clientsListResponse = await clientService.getAllClients();
      const clientsList = clientsListResponse.data;

      if (!clientsList || clientsList.length === 0) {
        toast.warning("Aucun client à exporter");
        setIsExporting(false);
        return;
      }

      toast.info(
        `Récupération des détails de ${clientsList.length} clients...`
      );

      // 2. Récupérer les détails complets de chaque client
      const allClientDetails = await fetchAllClientDetails(clientsList);

      if (allClientDetails.length === 0) {
        toast.error("Aucune donnée récupérée");
        setIsExporting(false);
        return;
      }

      toast.info("Génération du fichier CSV...");

      // 3. Générer et télécharger le CSV
      clientExportService.exportClientsToCSV(allClientDetails);

      toast.success(
        `Export réussi : ${allClientDetails.length} client(s) exporté(s)`
      );
    } catch (error) {
      console.error("Erreur lors de l'export:", error);
      toast.error("Erreur lors de l'export des clients");
    } finally {
      setIsExporting(false);
      setExportProgress({ current: 0, total: 0 });
    }
  }, [isExporting]);

  return {
    isExporting,
    exportProgress,
    exportAllClients,
  };
};

export default useClientExport;
